import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { UncontrolledAccordion, AccordionItem, Row, Col, Button } from 'reactstrap';
import paginationFactory, { PaginationProvider } from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import 'boxicons';
import styled from 'styled-components';

import { useAuth } from 'hooks/useAuth';
import { getFirebaseBackend } from '../../../../helpers/firebaseHelper';
import { AccordionTitle, AccordionTable, ActionWrapper, AccordionNameRow } from './styles';

import {
  statusColorClass,
  statusNumber,
  priorityColorClass,
  priorityLetter,
  priorityNumber,
} from '../../../../constants';
import TaskContainerDatePicker from 'components/Task-Container-Date-Picker';
import UserAvatar from 'components/UserAvatar';
import { getTaskColumns } from '../../config/taskTableColumns';
import { getDefaultSorted, getPageOptions } from 'pages/tasks/config/tableConfig';
import AvatarPlaceholder from 'assets/images/Avatar.svg';

const PRIORITY_CLASSES = {
  low: 'priority--low',
  normal: 'priority--medium',
  high: 'priority--high',
  urgent: 'priority--urgent',
};
const PRIORITY_NUMBERS = {
  low: 0,
  normal: 1,
  high: 2,
  urgent: 3,
};
const PRIORITY_LABELS = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

const AccordionList = ({ column, data }) => {
  const firebaseHelper = getFirebaseBackend();
  const { t: translate } = useTranslation();
  const { user } = useAuth();
  const accountId = user?.account;
  const [tasks, setTasks] = useState([]);

  const impactElem = (meetingImpact) => (
    <div className={`${PRIORITY_CLASSES[meetingImpact]} badge rounded-pill font-size-14 px-3 py-1`}>
      {translate(PRIORITY_LABELS[meetingImpact])}
    </div>
  );

  const dateDeliveryElem = (meetingDateDelivery, meetingId) => (
    <TaskContainerDatePicker date={meetingDateDelivery} id={meetingId} isDateDelivery anyDate />
  );

  const dateImplementationElem = (meetingDateImplementation, meetingId) => (
    <TaskContainerDatePicker
      date={meetingDateImplementation}
      id={meetingId}
      isDateImplementation
      anyDate
    />
  );

  const handleTaskRemove = (id) => {
    firebaseHelper.removeTask(accountId, id);
  };

  const avatarElem = (assignedTo) => {
    return (
      <div className="avatar-group justify-content-center">
        {_.map(assignedTo, (member, index) => {
          if (index > 1) return;

          return (
            <div className="avatar-group-item" key={index}>
              <img
                src={member?.profilePicture}
                alt={member?.name}
                className="rounded-circle avatar-xs"
              />
            </div>
          );
        })}
        {_.size(assignedTo) > 2 && (
          <div className="avatar-group-item">
            <div className="avatar-xs">
              <span
                className="avatar-title rounded-circle text-white font-size-16"
                style={{ backgroundColor: 'black' }}
              >
                {`${_.size(assignedTo) - 2}+`}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const tasksData = data ?? [];

    const newData = tasksData.map((task) => {
      const assignedTo = task.assignees || [];
      const implementationObj = task?.custom_fields?.find(
        (field) => field.name === 'Data de implementação',
      );
      const implementationDate = implementationObj?.value
        ? new Date(+implementationObj.value)
        : null;
      const dueDate = task.due_date ? new Date(+task.due_date) : null;

      return {
        position: task.position,
        name: <AccordionNameRow>{task.name}</AccordionNameRow>,
        nameValue: task.name,
        statusValue: task.status,
        assignedTo: assignedTo.length ? (
          avatarElem(assignedTo)
        ) : (
          <img src={AvatarPlaceholder} alt="Avatar Placeholder" />
        ),
        assignedToValue: assignedTo.length,
        complexityValue: task.complexity ? PRIORITY_NUMBERS[task.complexity] : 0,
        impact: task?.priority?.priority ? impactElem(task?.priority?.priority) : '-',
        impactValue: task.impact ? PRIORITY_NUMBERS[task?.priority?.priority] : 0,
        delivery: dateDeliveryElem(dueDate, task.id),
        deliveryValue: dueDate ?? 0,
        implementation: dateImplementationElem(implementationDate, task.id),
        implementationValue: implementationDate ?? 0,
      };
    });
    setTasks(newData);
  }, [data]);

  return (
    <UncontrolledAccordionStyled
      className="mb-4 overflow-visible"
      defaultOpen={['1', '2', '3', '4', '5']}
      stayOpen
    >
      <AccordionItem>
        <AccordionTitle targetId="1" className="p-0 border-0">
          {`${translate(column)} (${data?.length ?? 0})`}
        </AccordionTitle>

        <AccordionTable accordionId="1">
          <Row>
            <Col lg="12" className="overflow-visible">
              <PaginationProvider
                pagination={paginationFactory(getPageOptions(tasks.length))}
                keyField="id"
                columns={getTaskColumns(translate)}
                data={tasks}
              >
                {({ paginationTableProps }) => (
                  <ToolkitProvider keyField="id" columns={getTaskColumns(translate)} data={tasks}>
                    {(toolkitProps) => (
                      <React.Fragment>
                        <Row>
                          <Col xl="12">
                            <div className="table-responsive overflow-visible">
                              <BootstrapTable
                                keyField="id"
                                responsive
                                hover
                                bordered={false}
                                striped={false}
                                defaultSorted={getDefaultSorted}
                                classes={'table align-middle table-nowrap'}
                                headerWrapperClasses={'thead-light'}
                                {...toolkitProps.baseProps}
                                {...paginationTableProps}
                              />
                            </div>
                          </Col>
                        </Row>
                      </React.Fragment>
                    )}
                  </ToolkitProvider>
                )}
              </PaginationProvider>
            </Col>
          </Row>
        </AccordionTable>
      </AccordionItem>
    </UncontrolledAccordionStyled>
  );
};

const UncontrolledAccordionStyled = styled(UncontrolledAccordion)`
  .accordion-button {
    box-shadow: none !important;
  }
`;

AccordionList.displayName = 'Accordion List Component';

AccordionList.propTypes = {
  column: PropTypes.string,
  data: PropTypes.array,
};

const arePropsEqual = (prevProps, nextProps) => {
  const isColumnEquals = prevProps.column === nextProps.column;
  const isDataEquals = JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data);

  return isColumnEquals && isDataEquals;
};

export default memo(AccordionList, arePropsEqual);
